<script>
import AdminLayout from '@layouts/admin-main'
import LevelInfo from '@components/admin/security-level-info'
import RestrictionInfo from '@components/admin/security-restriction-info'
import {
  fetchLevels,
  deleteLevel,
  fetchRestrictions,
  deleteRestriction,
} from '@apis/admin/security'

export default {
  page: {
    title: 'Admin::Market',
    meta: [{ name: 'description', content: 'Admin::Market' }],
  },
  components: {
    AdminLayout,
    LevelInfo,
    RestrictionInfo,
  },
  data() {
    return {
      levels: [],
      target_level_seq: 0,
      show_level_info: false,
      restrictions: [],
      target_restriction_seq: 0,
      show_restriction_info: false,
    }
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      Promise.all([this.loadLevels(), this.loadRestrictions()]).catch((e) =>
        this.$toasted.error(e.message)
      )
    },
    async loadLevels() {
      const { data } = await fetchLevels()

      this.levels = data
    },
    async loadRestrictions() {
      const { data } = await fetchRestrictions()

      this.restrictions = data
    },
    handleCreateLevel() {
      this.show_level_info = true
      this.target_level_seq = null
    },
    handleUpdateLevel({ seq }) {
      this.show_level_info = true
      this.target_level_seq = seq
    },
    handleLevelSubmit(seq) {
      this.init()
      this.target_level_seq = seq
    },
    async handleDeleteLevel(seq) {
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.confirm_delete'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await deleteLevel(seq)
        await this.loadLevels()
      } catch (e) {
        this.$toasted.error(e.message)
      }
    },
    handleCreateRestriction() {
      this.show_restriction_info = true
      this.target_restriction_seq = null
    },
    handleUpdateRestriction({ seq }) {
      this.show_restriction_info = true
      this.target_restriction_seq = seq
    },
    handleRestrictionSubmit(seq) {
      this.init()
      this.target_restriction_seq = seq
    },
    async handleDeleteRestriction(seq) {
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.confirm_delete'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await deleteRestriction(seq)
        await this.loadRestrictions()
      } catch (e) {
        this.$toasted.error(e.message)
      }
    },
  },
}
</script>

<template>
  <AdminLayout>
    <div :class="$style.adminLeftContent">
      <h3 :class="$style.xeAdmin">{{ $t('admin.restriction_manage') }}</h3>
      <table cellspacing="0" cellpadding="0" :class="$style.crossTable">
        <thead>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.restriction_list_order') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.restriction_level') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.restriction_title') }}</div></th
            >
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.button, $style.black]"
                  @click.prevent="handleCreateRestriction"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="restriction in restrictions" :key="restriction.seq">
            <td :class="[$style.tCenter, $style.nowrap]">{{
              restriction.list_order
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]"
              >{{ restriction.level_start || '' }} ~
              {{ restriction.level_end || '' }}</td
            >
            <td :class="[$style.wide]">{{ restriction.title }}</td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                href="#"
                :class="[$style.button]"
                @click.prevent="handleUpdateRestriction(restriction)"
                ><span>{{ $t('admin.modify') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="handleDeleteRestriction(restriction.seq)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!restrictions.length">
            <td :colspan="4" :class="$style.tCenter">{{
              $t('admin.no_restriction')
            }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div :class="$style.adminRightExtra">
      <h3 :class="$style.xeAdmin">{{ $t('admin.level_manage') }}</h3>
      <table cellspacing="0" cellpadding="0" :class="$style.crossTable">
        <thead>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.level_list_order') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.level') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.level_title') }}</div></th
            >
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.button, $style.black]"
                  @click.prevent="handleCreateLevel"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="level in levels" :key="level.seq">
            <td :class="[$style.tCenter, $style.nowrap]">{{
              level.list_order
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{ level.level }}</td>
            <td :class="[$style.wide]">{{ level.title }}</td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                href="#"
                :class="[$style.button]"
                @click.prevent="handleUpdateLevel(level)"
                ><span>{{ $t('admin.modify') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="handleDeleteLevel(level.seq)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!levels.length">
            <td :colspan="4" :class="$style.tCenter">{{
              $t('admin.no_level')
            }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <ElDialog
      :title="
        target_restriction_seq
          ? $t('admin.restriction_modify')
          : $t('admin.restriction_enroll')
      "
      :visible.sync="show_restriction_info"
    >
      <RestrictionInfo
        v-if="show_restriction_info"
        :key="target_restriction_seq"
        :restriction_seq="target_restriction_seq"
        @submit="handleLevelSubmit"
      ></RestrictionInfo>
    </ElDialog>
    <ElDialog
      :title="
        target_level_seq ? $t('admin.level_modify') : $t('admin.level_enroll')
      "
      :visible.sync="show_level_info"
    >
      <LevelInfo
        v-if="show_level_info"
        :key="target_level_seq"
        :level_seq="target_level_seq"
        @submit="handleLevelSubmit"
      ></LevelInfo>
    </ElDialog>
  </AdminLayout>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}
</style>
